import { z } from 'zod';
import { zObjectId } from './primitives.js';
/** `GET /api/admin/users`: search by email or name, newest accounts first. */
export const zAdminUsersQuery = z.object({
    q: z.string().trim().max(120).optional(),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
/** `PATCH /api/admin/users/:id`. You can't change your own role or disable yourself. */
export const zAdminUpdateUser = z
    .object({
    role: z.enum(['admin', 'user']).optional(),
    disabled: z.boolean().optional(),
})
    .refine((value) => value.role !== undefined || value.disabled !== undefined, {
    message: 'Nothing to change',
});
/** `POST /api/admin/users/:id/bot`: emergency stop and/or force paper. Never switches anyone to live. */
export const zAdminBotAction = z
    .object({
    botState: z.literal('off').optional(),
    executionMode: z.literal('paper').optional(),
})
    .refine((value) => value.botState !== undefined || value.executionMode !== undefined, {
    message: 'Nothing to change',
});
/** `POST /api/admin/users/:id/demo-data`: copy another account's traders and closed paper history. */
export const zAdminLoadDemo = z.object({ sourceUserId: zObjectId });
/** `DELETE /api/admin/users/:id`: type the account's email to confirm. */
export const zAdminDeleteUser = z.object({
    confirmEmail: z.string().trim().toLowerCase().min(3).max(254),
});
/**
 * Domains bulk cleanup may target: reserved for documentation and testing, so a real customer's
 * address can never match. A suffix's domain must be one of these or a subdomain of one.
 */
export const CLEANUP_TEST_DOMAINS = [
    'example.com',
    'example.org',
    'example.net',
    'test',
    'invalid',
    'localhost',
];
/** Whether `suffix` (e.g. `@example.com`, `-qa@lab.test`) ends in a test domain. */
export function isTestEmailSuffix(suffix) {
    const at = suffix.lastIndexOf('@');
    if (at < 0)
        return false;
    const domain = suffix
        .slice(at + 1)
        .trim()
        .toLowerCase();
    return CLEANUP_TEST_DOMAINS.some((allowed) => domain === allowed || domain.endsWith(`.${allowed}`));
}
/**
 * An email suffix such as `@example.com`: an `@` followed by the whole domain, which must be a test
 * domain (see `CLEANUP_TEST_DOMAINS`), so a typo can never match real accounts.
 */
export const zEmailSuffix = z
    .string()
    .trim()
    .toLowerCase()
    .min(4)
    .max(254)
    .regex(/^[^\s@]*@[a-z0-9-]+(\.[a-z0-9-]+)*$/, 'Use an email ending such as @example.com')
    .refine(isTestEmailSuffix, {
    message: `Only test domains can be cleaned up: ${CLEANUP_TEST_DOMAINS.join(', ')} or their subdomains`,
});
/** `POST /api/admin/cleanup/preview`. */
export const zAdminCleanupPreview = z.object({ emailSuffix: zEmailSuffix });
/** `POST /api/admin/cleanup`: deletes each matching account through the guarded delete. */
export const zAdminCleanup = zAdminCleanupPreview.extend({ confirm: z.literal('DELETE') });
/** At most this many accounts are deleted per cleanup run (run it again for the rest). */
export const ADMIN_CLEANUP_BATCH = 100;
/** `GET /api/admin/audit`. */
export const zAdminAuditQuery = z.object({
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
// ── Dashboard (`GET /api/admin/overview`) ──────────────────────────────────────────────────────
/** `GET /api/admin/overview`: platform figures over the last `days` UTC days (today included). */
export const zAdminOverviewQuery = z.object({
    days: z.coerce.number().int().min(7).max(90).default(30),
});
// ── Wallet directory (`GET /api/admin/wallets`) ───────────────────────────────────────────────
/**
 * `GET /api/admin/wallets`: every custodial wallet on the platform, newest first. `q` matches the
 * public key (any part), the wallet's label, or its owner's email or name.
 */
export const zAdminWalletsQuery = z.object({
    q: z.string().trim().max(120).optional(),
    status: z.enum(['active', 'archived', 'all']).default('active'),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
//# sourceMappingURL=admin.js.map